import React, { useEffect, useState } from "react";
import {
  PDFViewer,
  Text,
  View,
  Document,
  StyleSheet,
  PDFDownloadLink,
} from "@react-pdf/renderer";
import { DateTime } from "luxon";
import { Link } from "react-router-dom";
import dataService from "../../../../helpers/dataService";
import Loader from "../../../common/Loader";
import PVDocV2 from "./PVDocV2";
import PVAnnexesV2 from "./PVAnnexesV2";
import colors from "../../../../helpers/colors";
import useStore from "../../../../context/useStore";

const sharedStyles = StyleSheet.create({
  title: {
    fontSize: 18,
    fontWeight: "bold",
    paddingTop: 20,
    paddingBottom: 10,
    textAlign: "center",
    color: colors.primary,
  },
  info: {
    fontSize: 11,
    paddingBottom: 5,
  },
  tableRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexDirection: "row",
  },
  tableRowCustom: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    flexDirection: "row",
  },
  tableHead: {
    textAlign: "left",
    color: "white",
    backgroundColor: "black",
  },
  tableCell2: {
    display: "flex",
    width: "49.99%",
    borderBottomWidth: 1,
    borderColor: "black",
    borderBottomStyle: "solid",
    padding: 3,
    fontSize: 11,
    height: "100%",
  },
  tableCell3: {
    display: "flex",
    width: "33.33%",
    borderBottomWidth: 1,
    borderColor: "black",
    borderBottomStyle: "solid",
    padding: 3,
    fontSize: 11,
    height: "100%",
  },
  tableCell4: {
    display: "flex",
    width: "24.99%",
    borderBottomWidth: 1,
    borderColor: "black",
    borderBottomStyle: "solid",
    padding: 3,
    fontSize: 11,
    height: "100%",
  },
  tableCell17: {
    display: "flex",
    borderBottomWidth: 1,
    borderColor: "black",
    borderBottomStyle: "solid",
    padding: 3,
    fontSize: 8,
    height: "100%",
  },
  textCenter: {
    textAlign: "center",
  },
  tableColumn: {
    display: "flex",
    flexDirection: "column",
  },
});

const MyDocV2 = ({ ep, inscriptions, orgas, isPreview, constants }) => {
  const title = (
    <>
      <View>
        <Text>PROCÈS VERBAL DE L'ÉQUIPE PLURIDISCIPLINAIRE</Text>
      </View>
      <View>
        <Text style={{ color: "black", fontSize: 13 }}>
          SECTEUR {ep.sector?.name || ep.sector} —{" "}
          {DateTime.fromISO(ep.beginAt)
            .setLocale("fr")
            .toFormat("dd LLLL yyyy")}
        </Text>
      </View>
      {isPreview && (
        <View
          style={{
            width: "100%",
            backgroundColor: "orange",
          }}
        >
          <Text
            style={{ fontSize: 14, color: "white", backgroundColor: "orange" }}
          >
            PRÉVISUALISATION — PV NON FINALISÉ
          </Text>
        </View>
      )}
    </>
  );

  return (
    <Document>
      <PVDocV2
        styles={sharedStyles}
        ep={ep}
        inscriptions={inscriptions}
        orgas={orgas}
        title={title}
        constants={constants}
      />
      <PVAnnexesV2
        styles={sharedStyles}
        ep={ep}
        inscriptions={inscriptions}
        title={title}
      />
    </Document>
  );
};

const EpPvV2 = (props) => {
  const epId = props.match.params.id;
  const [state] = useStore();
  const constants = state.constants.items;

  const [ep, setEp] = useState(null);
  const [inscriptions, setInscriptions] = useState([]);
  const [orgas, setOrgas] = useState([]);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [docReady, setDocReady] = useState(false);
  const [displayPreview, setDisplayPreview] = useState(false);
  const [pdfKey, setPdfKey] = useState(1);

  useEffect(() => {
    setIsLoading(true);
    dataService.get(
      `eps/${epId}`,
      (data) => {
        setEp(data);

        dataService.get(
          `eps/${epId}/lister-inscriptions`,
          (inscData) => {
            setInscriptions(Array.isArray(inscData) ? inscData : []);

            dataService.get(
              `organizations`,
              (orgData) => {
                setOrgas(Array.isArray(orgData) ? orgData : []);
                setIsLoading(false);
              },
              (err) => {
                setErrors(err);
                setIsLoading(false);
              },
            );
          },
          (err) => {
            setErrors(err);
            setIsLoading(false);
          },
        );
      },
      (err) => {
        setErrors(err);
        setIsLoading(false);
      },
    );
  }, [epId]);

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Loader />
        <p className="mt-3 text-muted">Chargement du procès-verbal…</p>
      </div>
    );
  }

  if (Object.keys(errors).length > 0 || !ep) {
    return (
      <div className="container mt-4">
        <Link to={`/ep-v2/${epId}`} className="btn btn-link">
          <i className="fa fa-arrow-left mr-2" />
          Retour à la session
        </Link>
        <div className="alert alert-danger mt-3">
          <i className="fa fa-exclamation-triangle mr-2" />
          Erreur lors du chargement du PV.
        </div>
      </div>
    );
  }

  const fileName = `PV_EP_${DateTime.fromISO(ep.beginAt)
    .setLocale("fr")
    .toFormat("dd-LLLL-yyyy")}_${ep.sector?.name || ep.sector}.pdf`;

  return (
    <div
      key={pdfKey}
      className="text-center pt-3 bg-white pb-3 px-3 mt-2 animated fadeIn faster"
    >
      <Link to={`/ep-v2/${epId}`} className="btn btn-link mb-3 d-inline-block">
        <i className="fa fa-arrow-left mr-2" />
        Retour à la session
      </Link>

      <h4 className="mb-3">
        PROCÈS VERBAL — SECTEUR {ep.sector?.name || ep.sector}{" "}
        {DateTime.fromISO(ep.beginAt).setLocale("fr").toFormat("dd LLLL yyyy")}{" "}
        à {ep.beginHourAt}h
        {ep.beginMinuteAt ? String(ep.beginMinuteAt).padStart(2, "0") : "00"}
      </h4>

      <div
        className="d-flex justify-content-center flex-column align-items-center"
        style={{ gap: "1rem" }}
      >
        <PDFDownloadLink
          className="btn btn-primary mb-2 animated zoomIn faster"
          document={
            <MyDocV2
              ep={ep}
              inscriptions={inscriptions}
              orgas={orgas}
              isPreview={false}
              constants={constants}
            />
          }
          fileName={fileName}
        >
          {({ loading, error }) => {
            if (error) setPdfKey((k) => k + 1);
            if (!loading) setDocReady(true);
            return loading ? (
              "Génération en cours…"
            ) : (
              <>
                <i className="fa fa-download mr-2" />
                Télécharger le PV
              </>
            );
          }}
        </PDFDownloadLink>

        {docReady && (
          <button
            className="btn btn-info mb-2 animated zoomIn faster"
            onClick={() => setDisplayPreview((v) => !v)}
          >
            <i
              className={`fa mr-2 ${displayPreview ? "fa-eye-slash" : "fa-eye"}`}
            />
            {displayPreview ? "Fermer" : "Voir dans"} la visionneuse
          </button>
        )}

        {displayPreview && (
          <div
            className="animated fadeIn fast"
            style={{ width: "100%", height: 700 }}
          >
            <PDFViewer style={{ width: "100%", height: "100%", border: 0 }}>
              <MyDocV2
                ep={ep}
                inscriptions={inscriptions}
                orgas={orgas}
                isPreview={false}
                constants={constants}
              />
            </PDFViewer>
          </div>
        )}
      </div>
    </div>
  );
};

export default EpPvV2;
